import { Button } from "@/components/ui/baseUi/button"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { Text } from "@/components/ui/baseUi/text"
import { BoxProgramVisibility } from "@/modules/boxProgrammation/boxProgramTypes"
import { getBoxProgrammationVisibilityExplanation } from "@/modules/boxProgrammation/boxProgramUtils"
import { BoxProgramPrivacyIcon } from "@/modules/boxProgrammation/components/boxProgramPrivacyIcon"
import type { BoxProgramFormFields } from "@/modules/boxProgrammation/formik/boxProgramFormTypes"
import { useBoxOffers } from "@modules/boxOffer/hooks/useBoxOffer"
import { BoxRestrictionTag } from "@modules/boxProgrammation/components/boxRestrictionTag"
import { word } from "@modules/core/utils/i18n"
import { ErrorCard } from "@modules/ui/components/error/errorCard"
import { useStore } from "@tanstack/react-store"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useState } from "react"

export const BoxProgramOfferForm = withForm({
  defaultValues: {} as BoxProgramFormFields,
  render: function BoxProgramOfferFormInner({ form }) {
    const [areOffersExpanded, setAreOffersExpanded] = useState(false)
    const { data: boxOffers, isError } = useBoxOffers()

    const offerIds = useStore(form.baseStore, (state) => state.values.offerIds)

    const toggleOffersList = () => setAreOffersExpanded((value) => !value)

    const onToggleOffer = (boxOfferId: string) => {
      const boxOfferIndex = offerIds.indexOf(boxOfferId)
      if (boxOfferIndex >= 0) {
        form.setFieldValue(
          "offerIds",
          offerIds.filter((id) => id !== boxOfferId),
        )
      } else {
        form.setFieldValue("offerIds", [...offerIds, boxOfferId])
      }
    }

    const onToggleAllOffers = () => {
      if (offerIds.length === boxOffers.length) {
        form.setFieldValue("offerIds", [])
      } else {
        form.setFieldValue(
          "offerIds",
          boxOffers.map((boxOffer) => boxOffer.id),
        )
      }
    }

    return (
      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" className="uppercase">
          {word("box.programmation.form.access.label")}
        </Text>

        {isError ? (
          <ErrorCard />
        ) : (
          <div>
            {/* Only showing the MEMBERS visibility option */}
            <div className="rounded-3xl border-2 border-borderDefault bg-neutral-50 p-4">
              <div className="flex items-start gap-6">
                <BoxProgramPrivacyIcon visibility={BoxProgramVisibility.MEMBERS} isSelected={true} />

                {getBoxProgrammationVisibilityExplanation(BoxProgramVisibility.MEMBERS)}

                <div className="mt-1 flex w-5/12 flex-col items-end">
                  <Text
                    fontStyle="BS"
                    className="text-textDefault-body"
                  >{`${offerIds.length.toString()}/${boxOffers.length.toString()} ${word("global.selected", {
                    count: offerIds.length === 0 ? 1 : offerIds.length,
                  })}`}</Text>

                  <Button variant="underline" size="xs" onClick={onToggleAllOffers} className="text-[0.625rem]">
                    {offerIds.length !== boxOffers.length
                      ? word("global.filters.selectAll")
                      : word("global.filters.unselectAll")}
                  </Button>
                </div>
              </div>

              <div className="my-4 flex flex-wrap items-center gap-2">
                {boxOffers
                  .filter((_, index) => (areOffersExpanded ? true : index < 4))
                  .map((boxOffer) => {
                    const isSelectedOffer = offerIds.indexOf(boxOffer.id) >= 0
                    return (
                      <BoxRestrictionTag
                        key={boxOffer.id}
                        boxOffer={boxOffer}
                        isSelected={isSelectedOffer}
                        onSelectRestriction={() => onToggleOffer(boxOffer.id)}
                      />
                    )
                  })}
              </div>

              {boxOffers.length > 4 && (
                <div
                  onClick={toggleOffersList}
                  className="flex w-full cursor-pointer flex-col items-center justify-center gap-1 transition-all duration-150"
                  onMouseEnter={(e) => e.currentTarget.classList.add("gap-2")}
                  onMouseLeave={(e) => e.currentTarget.classList.remove("gap-2")}
                >
                  <Text fontStyle="BS">
                    {areOffersExpanded
                      ? word("box.programmation.form.offers.showLess")
                      : word("box.programmation.form.offers.showMore")}
                  </Text>
                  {areOffersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  },
})
